// gitlab-evidence-request-helpers.ts
//
// Split out of gitlab-merge-request-evidence-client.ts (M2 fix) to keep
// that file under the 200-line convention. Thin, side-effect-free helpers:
// one timed fetch-and-parse-as-json call, and two schema-validated array
// unwrappers that fail soft to `[]` on a non-2xx status or a shape
// mismatch (the caller decides what an empty result means).
import { z } from "zod";

export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

export type RequestJsonResult = { status: number; body: unknown } | { status: -1; body: undefined };

/** One timed REST call with the GitLab `PRIVATE-TOKEN` header attached. */
export async function requestJson(
  fetchImpl: FetchLike,
  url: string,
  token: string,
  timeoutMs: number,
): Promise<RequestJsonResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(url, {
      headers: { "PRIVATE-TOKEN": token },
      signal: controller.signal,
    });
    const body = await res.json().catch(() => undefined);
    return { status: res.status, body };
  } catch {
    return { status: -1, body: undefined };
  } finally {
    clearTimeout(timer);
  }
}

/** Non-2xx or schema mismatch -> `[]`, never a throw (caller treats absence as "no evidence yet"). */
export function arraySchemaOrEmpty<T extends z.ZodTypeAny>(
  schema: T,
  res: RequestJsonResult,
): z.infer<T>[] {
  if (res.status < 200 || res.status >= 300) return [];
  const parsed = z.array(schema).safeParse(res.body);
  return parsed.success ? parsed.data : [];
}
